// Keep these messages identical for browser completion and durable server delivery.
// Imported samples are user data; delivery/authorization belongs to the caller.
export interface CSVImportCompletion {
    tableId: number;
    count: number;
    linkedContactsCount?: number;
    companyCount?: number;
    inputRowCount?: number;
    type: "contacts" | "companies";
    fileName: string;
    campaignId?: number;
    signalItemId?: number;
    emailFinderOptions?: { enrichEmail: boolean; replaceInvalidEmail: boolean; verifyEmail: true };
    sampleRows?: Array<Record<string, string>>;
    fieldAvailability?: {
        totalRows: number;
        importableRows: number;
        skippedRows: number;
        email: { availableCount: number; missingCount: number; availablePercent: number };
        linkedinUrl: { availableCount: number; missingCount: number; availablePercent: number };
        companyUrl: { availableCount: number; missingCount: number; availablePercent: number };
        contactName: { availableCount: number; missingCount: number; availablePercent: number };
        hasAnyEmail: boolean;
        hasAnyLinkedInUrl: boolean;
        hasAnyCompanyUrl: boolean;
        hasAnyContactName: boolean;
    };
}

export interface ExclusionImportCompletion {
    count: number;
    type: "domains" | "contacts";
    fileName: string;
}

export function buildCSVImportCompletionMessage(result: CSVImportCompletion): string {
    const typeLabel = result.type === "contacts" ? "contacts" : "companies";
    const displayCount = result.linkedContactsCount ?? result.count;
    const parts = [`[CSV Upload Complete] Uploaded ${displayCount} ${typeLabel} from "${result.fileName}".`];
    if (result.type === "contacts" && result.companyCount) {
        parts.push(`These ${displayCount} contacts belong to ${result.companyCount} unique companies.`);
    }
    if (result.inputRowCount && result.inputRowCount > displayCount) {
        const skipped = result.inputRowCount - displayCount;
        parts.push(`${skipped} rows from the file were skipped because they had no reachable contact info (no email, LinkedIn, or company domain).`);
    }
    if (result.emailFinderOptions) {
        parts.push(`User-selected uploaded-contacts options: ${JSON.stringify(result.emailFinderOptions)}. Preserve these options when creating the uploaded-contacts signal. Do not enable invalid-email replacement without explicit opt-in.`);
    }
    if (result.fieldAvailability) {
        const availability = result.fieldAvailability;
        parts.push(
            [
                "Contact field availability:",
                `email ${availability.email.availableCount}/${availability.totalRows} (${availability.email.availablePercent}%)`,
                `LinkedIn URL ${availability.linkedinUrl.availableCount}/${availability.totalRows} (${availability.linkedinUrl.availablePercent}%)`,
                `company URL ${availability.companyUrl.availableCount}/${availability.totalRows} (${availability.companyUrl.availablePercent}%)`,
                `contact name ${availability.contactName.availableCount}/${availability.totalRows} (${availability.contactName.availablePercent}%)`,
                `importable rows ${availability.importableRows}/${availability.totalRows}`,
            ].join(" "),
        );
        parts.push(
            "Respect the user-selected enrichment options above. If different options are needed, ask first: email campaigns require email availability or enrichEmail; LinkedIn campaigns require LinkedIn URL availability or enrichLinkedIn. enrichLinkedIn needs email availability, so if both email and LinkedIn URL are missing, missing-email enrichment is needed first.",
        );
    }
    if (result.sampleRows && result.sampleRows.length > 0) {
        parts.push(`Sample imported rows (representative, truncated): ${JSON.stringify(result.sampleRows)}`);
    }
    parts.push(`Table ID: ${result.tableId}.`);
    if (result.campaignId) {
        parts.push(`Attached to campaign ${result.campaignId}.`);
    }
    if (result.signalItemId) {
        parts.push(`Uploaded-contacts signal created (item ID: ${result.signalItemId}).`);
    }
    return parts.join(" ");
}

export function buildExclusionImportCompletionMessage(result: ExclusionImportCompletion): string {
    const typeLabel = result.type === "domains" ? "domains" : "contacts";
    return `[Exclusion Upload Complete] Excluded ${result.count} ${typeLabel} from "${result.fileName}".`;
}
